import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';

// Tailwind utility presets for consistent styling
const btnPrimary =
  'inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const btnSecondary =
  'inline-flex items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const btnSuccess =
  'inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const inputClass =
  'block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6';
const selectClass = inputClass;
const sectionCard = 'rounded-xl border border-gray-200 bg-white shadow-sm';

// Entity field definitions for mapping
const ENTITY_FIELDS = {
  clients: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier from your old system' },
    { value: 'client_type', label: 'Client Type', description: 'individual or organizational', required: true },
    { value: 'first_name', label: 'First Name', description: 'For individual clients' },
    { value: 'last_name', label: 'Last Name', description: 'For individual clients' },
    { value: 'email', label: 'Email', description: 'Client email address', required: true },
    { value: 'phone_number', label: 'Phone Number', description: 'Contact phone' },
    { value: 'address', label: 'Address', description: 'Physical address' },
    { value: 'organization_name', label: 'Organization Name', description: 'For organizational clients' },
  ],
  cases: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier from your old system' },
    { value: 'title', label: 'Case Title', description: 'Name of the case', required: true },
    { value: 'client_email', label: 'Client Email', description: 'Email of the client', required: true },
    { value: 'case_type', label: 'Case Type', description: 'Type of legal case' },
    { value: 'case_number', label: 'Case Number', description: 'Case reference number' },
  ],
  tasks: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier from your old system' },
    { value: 'title', label: 'Task Title', description: 'Name of the task', required: true },
    { value: 'description', label: 'Description', description: 'Task details' },
    { value: 'case_title', label: 'Case Title', description: 'Associated case name' },
    { value: 'assignee_username', label: 'Assignee Username', description: 'Who is assigned' },
    { value: 'due_date', label: 'Due Date', description: 'When task is due' },
    { value: 'status', label: 'Status', description: 'Task status' },
    { value: 'priority', label: 'Priority', description: 'Task priority' },
  ],
  invoices: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier from your old system' },
    { value: 'invoice_number', label: 'Invoice Number', description: 'Invoice reference', required: true },
    { value: 'client_email', label: 'Client Email', description: 'Client email', required: true },
    { value: 'issue_date', label: 'Issue Date', description: 'When invoice was issued' },
    { value: 'due_date', label: 'Due Date', description: 'Payment due date' },
    { value: 'status', label: 'Status', description: 'Invoice status' },
    { value: 'subtotal', label: 'Subtotal', description: 'Amount before tax' },
    { value: 'tax_amount', label: 'Tax Amount', description: 'Tax amount' },
    { value: 'total_amount', label: 'Total Amount', description: 'Total including tax' },
    { value: 'currency', label: 'Currency', description: 'Currency code' },
  ],
  service_products: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier' },
    { value: 'name', label: 'Name', description: 'Service/product name', required: true },
    { value: 'description', label: 'Description', description: 'Details' },
    { value: 'price', label: 'Price', description: 'Unit price', required: true },
    { value: 'type', label: 'Type', description: 'Service or Product' },
    { value: 'category', label: 'Category', description: 'Category' },
    { value: 'is_active', label: 'Is Active', description: 'true/false' },
  ],
  expenses: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier' },
    { value: 'case_title', label: 'Case Title', description: 'Associated case' },
    { value: 'client_email', label: 'Client Email', description: 'Client email' },
    { value: 'description', label: 'Description', description: 'Expense details', required: true },
    { value: 'date', label: 'Date', description: 'When expense occurred', required: true },
    { value: 'amount', label: 'Amount', description: 'Expense amount', required: true },
    { value: 'category', label: 'Category', description: 'Expense category' },
    { value: 'billable', label: 'Billable', description: 'true/false' },
  ],
  payments: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier' },
    { value: 'invoice_number', label: 'Invoice Number', description: 'Associated invoice', required: true },
    { value: 'amount', label: 'Amount', description: 'Payment amount', required: true },
    { value: 'payment_date', label: 'Payment Date', description: 'When paid', required: true },
    { value: 'payment_method', label: 'Payment Method', description: 'How paid' },
    { value: 'notes', label: 'Notes', description: 'Additional notes' },
  ],
  time_entries: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier' },
    { value: 'case_title', label: 'Case Title', description: 'Associated case' },
    { value: 'client_email', label: 'Client Email', description: 'Client email' },
    { value: 'service_product', label: 'Service/Product', description: 'Service provided' },
    { value: 'description', label: 'Description', description: 'Work description', required: true },
    { value: 'start_time', label: 'Start Time', description: 'When started', required: true },
    { value: 'end_time', label: 'End Time', description: 'When ended', required: true },
    { value: 'is_billable', label: 'Is Billable', description: 'true/false' },
  ],
  beneficial_owners: [
    { value: 'external_id', label: 'External ID', description: 'Unique identifier' },
    { value: 'organization_email', label: 'Organization Email', description: 'Org email', required: true },
    { value: 'first_name', label: 'First Name', description: 'Owner first name', required: true },
    { value: 'last_name', label: 'Last Name', description: 'Owner last name', required: true },
    { value: 'email', label: 'Email', description: 'Owner email' },
    { value: 'phone_number', label: 'Phone Number', description: 'Contact phone' },
    { value: 'physical_residential_address', label: 'Physical Address', description: 'Residential address' },
    { value: 'id_or_passport_number', label: 'ID/Passport Number', description: 'ID number' },
    { value: 'id_type', label: 'ID Type', description: 'Type of ID' },
    { value: 'country_of_issue', label: 'Country of Issue', description: 'ID country' },
    { value: 'id_expiry_date', label: 'ID Expiry Date', description: 'When ID expires' },
    { value: 'date_of_birth', label: 'Date of Birth', description: 'DOB' },
    { value: 'nationality', label: 'Nationality', description: 'Nationality' },
    { value: 'is_director', label: 'Is Director', description: 'true/false' },
    { value: 'is_shareholder', label: 'Is Shareholder', description: 'true/false' },
    { value: 'percentage_of_shares', label: 'Percentage of Shares', description: 'Share %' },
    { value: 'ownership_type', label: 'Ownership Type', description: 'Direct/Indirect' },
  ],
};

// Auto-matching logic - attempts to guess the right field based on header name
function autoMatchColumn(csvHeader, entityFields) {
  const normalized = csvHeader.toLowerCase().trim().replace(/[_\s-]+/g, '');
  
  // Try exact match first
  const exactMatch = entityFields.find(field => 
    field.value.toLowerCase().replace(/[_\s-]+/g, '') === normalized
  );
  if (exactMatch) return exactMatch.value;
  
  // Try partial matches with common aliases
  const aliases = {
    'firstname': 'first_name',
    'surname': 'last_name',
    'lastname': 'last_name',
    'phone': 'phone_number',
    'tel': 'phone_number',
    'mobile': 'phone_number',
    'emailaddress': 'email',
    'mail': 'email',
    'company': 'organization_name',
    'companyname': 'organization_name',
    'org': 'organization_name',
    'organisation': 'organization_name',
    'casenumber': 'case_number',
    'caseno': 'case_number',
    'caseid': 'case_number',
    'casetitle': 'title',
    'casename': 'title',
    'clientemail': 'client_email',
    'client': 'client_email',
    'assignee': 'assignee_username',
    'assigned': 'assignee_username',
    'duedate': 'due_date',
    'deadline': 'due_date',
    'invoicenumber': 'invoice_number',
    'invoiceno': 'invoice_number',
    'issuedate': 'issue_date',
    'taxamount': 'tax_amount',
    'tax': 'tax_amount',
    'vat': 'tax_amount',
    'totalamount': 'total_amount',
    'total': 'total_amount',
    'legacyid': 'external_id',
    'oldid': 'external_id',
    'externalid': 'external_id',
  };
  
  const aliasMatch = aliases[normalized];
  if (aliasMatch && entityFields.find(f => f.value === aliasMatch)) {
    return aliasMatch;
  }
  
  // Try contains match
  const containsMatch = entityFields.find(field => {
    const fieldNorm = field.value.toLowerCase().replace(/[_\s-]+/g, '');
    return normalized.includes(fieldNorm) || fieldNorm.includes(normalized);
  });
  if (containsMatch) return containsMatch.value;
  
  return ''; // No match - user must select manually
}

// Step indicator component
function StepIndicator({ currentStep, steps }) {
  return (
    <nav aria-label="Progress" className="mb-8">
      <ol role="list" className="flex items-center justify-center">
        {steps.map((step, index) => (
          <li key={step.id} className={`${index !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''} relative`}>
            {index !== steps.length - 1 && (
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className={`h-0.5 w-full ${currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'}`} />
              </div>
            )}
            <div className="relative flex items-center justify-center">
              <span
                className={`${
                  currentStep > step.id
                    ? 'bg-blue-600 text-white'
                    : currentStep === step.id
                    ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                    : 'bg-gray-200 text-gray-500'
                } flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-all`}
                aria-current={currentStep === step.id ? 'step' : undefined}
              >
                {currentStep > step.id ? (
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  step.id
                )}
              </span>
            </div>
            <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-32 text-center text-xs font-medium text-gray-900">
              {step.name}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Privacy assurance banner
function PrivacyBanner() {
  return (
    <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-green-800">Your Data is Private & Secure</h3>
          <div className="mt-2 text-xs text-green-700">
            <ul className="list-disc pl-5 space-y-1">
              <li>Files are processed in memory only - not saved to disk</li>
              <li>Preview your data before committing any changes</li>
              <li>All imports are scoped to your firm only</li>
              <li>Actions are logged for audit purposes (metadata only, not content)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main component
export default function DataOnboardingTool({ authAxios, showFormMessage }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [entity, setEntity] = useState('clients');
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [previewResults, setPreviewResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [createClientsIfMissing, setCreateClientsIfMissing] = useState(false);
  const [allowMissingCase, setAllowMissingCase] = useState(false);
  const [missingDependencies, setMissingDependencies] = useState(null);

  const steps = [
    { id: 1, name: 'Upload' },
    { id: 2, name: 'Map Columns' },
    { id: 3, name: 'Preview' },
    { id: 4, name: 'Commit' },
  ];

  const entityFields = ENTITY_FIELDS[entity] || [];

  // Reset when entity changes
  useEffect(() => {
    setCurrentStep(1);
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setColumnMapping({});
    setPreviewResults(null);
    setMissingDependencies(null);
    
    // Try to restore saved mapping for this entity
    const savedMapping = localStorage.getItem(`import_mapping_${entity}`);
    if (savedMapping) {
      try {
        const mapping = JSON.parse(savedMapping);
        setColumnMapping(mapping);
      } catch (e) {
        console.error('Failed to restore mapping:', e);
      }
    }
  }, [entity]);

  // Step 1: Parse CSV file client-side
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setIsLoading(true);

    Papa.parse(file, {
      complete: (results) => {
        const headers = results.meta.fields || [];
        const data = results.data.filter(row => {
          // Filter out empty rows
          return Object.values(row).some(val => val !== null && val !== undefined && val !== '');
        });

        setCsvHeaders(headers);
        setCsvData(data);

        // Auto-generate mapping suggestions
        const autoMapping = {};
        headers.forEach(header => {
          const suggestion = autoMatchColumn(header, entityFields);
          if (suggestion) {
            autoMapping[header] = suggestion;
          }
        });
        setColumnMapping(autoMapping);

        setIsLoading(false);
        setCurrentStep(2);
        showFormMessage('success', `Parsed ${data.length} rows from ${file.name}`);
      },
      header: true,
      skipEmptyLines: true,
      error: (error) => {
        console.error('CSV parsing error:', error);
        showFormMessage('danger', `Failed to parse CSV: ${error.message}`);
        setIsLoading(false);
      }
    });
  };

  // Update column mapping
  const updateMapping = (csvHeader, systemField) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvHeader]: systemField
    }));
  };

  // Step 3: Run dry-run preview
  const runPreview = async () => {
    setIsLoading(true);
    setProgress(0);
    setPreviewResults(null);
    setMissingDependencies(null);

    // Save mapping to localStorage for sticky behavior
    localStorage.setItem(`import_mapping_${entity}`, JSON.stringify(columnMapping));

    try {
      // Send CSV + mapping to backend for dry-run
      const formData = new FormData();
      formData.append('csv_file', csvFile);
      formData.append('entity', entity);
      formData.append('dry_run', '1');
      formData.append('mapping', JSON.stringify(columnMapping));

      if (entity === 'cases') {
        formData.append('create_clients_if_missing', createClientsIfMissing ? '1' : '0');
      }
      if (entity === 'tasks') {
        formData.append('allow_missing_case', allowMissingCase ? '1' : '0');
      }

      const response = await authAxios.post('imports/csv/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      });

      setPreviewResults(response.data);
      
      // Check for missing dependencies
      if (response.data.missing_dependencies) {
        setMissingDependencies(response.data.missing_dependencies);
      }

      setCurrentStep(3);
      showFormMessage('success', 'Preview complete! Review the results below.');
    } catch (error) {
      console.error('Preview failed:', error);
      const msg = error.response?.data?.error || 'Preview failed. Please check your data and try again.';
      showFormMessage('danger', msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Final commit
  const runCommit = async () => {
    setIsLoading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);
      formData.append('entity', entity);
      formData.append('dry_run', '0');
      formData.append('mapping', JSON.stringify(columnMapping));

      if (entity === 'cases') {
        formData.append('create_clients_if_missing', createClientsIfMissing ? '1' : '0');
      }
      if (entity === 'tasks') {
        formData.append('allow_missing_case', allowMissingCase ? '1' : '0');
      }

      const response = await authAxios.post('imports/csv/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      });

      setPreviewResults(response.data);
      setCurrentStep(4);
      showFormMessage('success', `Successfully imported ${response.data.created || 0} ${entity}!`);
    } catch (error) {
      console.error('Import failed:', error);
      const msg = error.response?.data?.error || 'Import failed. Please try again.';
      showFormMessage('danger', msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Download error CSV
  const downloadErrorCsv = () => {
    if (!previewResults?.row_results) return;

    const errorRows = previewResults.row_results.filter(r => r.status === 'error' || r.status === 'skipped');
    if (errorRows.length === 0) {
      showFormMessage('info', 'No errors to download.');
      return;
    }

    // Rebuild CSV with only error rows
    const errorData = errorRows.map(r => csvData[r.row - 2]); // Account for 0-index and header row
    const csv = Papa.unparse(errorData, { header: true });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entity}_errors.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadMissingDependenciesCsv = () => {
    if (!missingDependencies) {
      showFormMessage('info', 'No missing dependencies to download.');
      return;
    }

    // Generate appropriate CSV based on missing dependency type
    let csvData = [];
    let filename = '';

    if (missingDependencies.clients && missingDependencies.clients.length > 0) {
      // Create CSV for missing clients with required headers
      csvData = missingDependencies.clients.map(email => ({
        email,
        client_type: 'individual',
        first_name: '',
        last_name: '',
        phone_number: '',
        address: '',
      }));
      filename = 'missing_clients.csv';
    } else if (missingDependencies.cases && missingDependencies.cases.length > 0) {
      // Create CSV for missing cases
      csvData = missingDependencies.cases.map(title => ({
        title,
        client_email: '',
        case_type: '',
        case_number: '',
      }));
      filename = 'missing_cases.csv';
    } else if (missingDependencies.assignees && missingDependencies.assignees.length > 0) {
      // Create CSV for missing assignees (staff members)
      csvData = missingDependencies.assignees.map(username => ({
        username,
        email: '',
        first_name: '',
        last_name: '',
        note: 'Create these users in User Management before importing tasks',
      }));
      filename = 'missing_assignees.csv';
    } else if (missingDependencies.service_products && missingDependencies.service_products.length > 0) {
      // Create CSV for missing service products
      csvData = missingDependencies.service_products.map(name => ({
        name,
        description: '',
        price: '',
        type: 'Service',
      }));
      filename = 'missing_service_products.csv';
    }

    if (csvData.length === 0) {
      showFormMessage('info', 'No missing dependencies data available.');
      return;
    }

    const csv = Papa.unparse(csvData, { header: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showFormMessage('success', `Downloaded ${filename} with ${csvData.length} missing items.`);
  };

  // Reset wizard
  const resetWizard = () => {
    setCurrentStep(1);
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setColumnMapping({});
    setPreviewResults(null);
    setMissingDependencies(null);
  };

  // Permission check — mirrors backend IsImportOperator: PARTNER, SUPPORT_STAFF, SUPERUSER.
  const canRunImports = () => {
    const role = localStorage.getItem('userRole');
    const su = localStorage.getItem('is_superuser') === 'true';
    return su || role === 'PARTNER' || role === 'SUPPORT_STAFF' || role === 'SUPERUSER';
  };

  if (!canRunImports()) {
    return (
      <div className="bg-white rounded shadow p-4">
        <h3 className="text-xl font-semibold mb-2">Data Migration Assistant</h3>
        <p className="text-gray-600">You don't have permission to access this tool.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-6 md:p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Data Migration Assistant</h2>
        <p className="text-sm text-gray-600">
          Import your existing data with ease. Upload your CSV, map the columns, preview the results, and commit when ready.
        </p>
      </div>

      <PrivacyBanner />

      <StepIndicator currentStep={currentStep} steps={steps} />

      {/* Step 1: Upload */}
      {currentStep === 1 && (
        <div className="mt-12 space-y-6">
          <div className={`${sectionCard} p-6`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Entity Type</h3>
            <p className="text-sm text-gray-600 mb-4">What type of data are you importing?</p>
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              className={selectClass}
            >
              <option value="clients">Clients</option>
              <option value="cases">Cases</option>
              <option value="tasks">Tasks</option>
              <option value="invoices">Invoices</option>
              <option value="service_products">Service Products</option>
              <option value="expenses">Expenses</option>
              <option value="payments">Payments</option>
              <option value="time_entries">Time Entries</option>
              <option value="beneficial_owners">Beneficial Owners</option>
            </select>
          </div>

          <div className={`${sectionCard} p-6`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Your CSV File</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a CSV file from your computer. We'll read the column headers and help you map them to our system fields.
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mt-2 text-sm font-medium text-gray-900">Click to upload or drag and drop</p>
                <p className="mt-1 text-xs text-gray-500">CSV files only</p>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Map Columns */}
      {currentStep === 2 && (
        <div className="mt-12 space-y-6">
          <div className={`${sectionCard} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Map Your Columns</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Match your CSV columns to our system fields. We've made some suggestions, but you can adjust them.
                </p>
              </div>
              <button onClick={() => setCurrentStep(1)} className={btnSecondary}>
                ← Back
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Found {csvHeaders.length} columns</strong> in your file with <strong>{csvData.length} data rows</strong>.
                Map at least the required fields to continue.
              </p>
            </div>

            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {csvHeaders.map((header, idx) => {
                const mappedField = columnMapping[header];
                const fieldInfo = entityFields.find(f => f.value === mappedField);
                
                return (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{header}</p>
                      {csvData[0] && csvData[0][header] && (
                        <p className="text-xs text-gray-500 truncate">
                          Example: {csvData[0][header]}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0 text-gray-400 text-sm">→</div>
                    
                    <div className="flex-1">
                      <select
                        value={columnMapping[header] || ''}
                        onChange={(e) => updateMapping(header, e.target.value)}
                        className={`${selectClass} text-sm`}
                      >
                        <option value="">🚫 Ignore this column</option>
                        {entityFields.map(field => (
                          <option key={field.value} value={field.value}>
                            {field.label} {field.required ? '(Required)' : ''}
                          </option>
                        ))}
                      </select>
                      {fieldInfo && (
                        <p className="text-xs text-gray-500 mt-1">{fieldInfo.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Dependency options */}
            {entity === 'cases' && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={createClientsIfMissing}
                    onChange={(e) => setCreateClientsIfMissing(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm">
                    <span className="font-medium text-gray-900">Create clients if missing</span>
                    <span className="block text-gray-600 mt-1">
                      If a case references a client email that doesn't exist, automatically create a prospect client record.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {entity === 'tasks' && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={allowMissingCase}
                    onChange={(e) => setAllowMissingCase(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm">
                    <span className="font-medium text-gray-900">Allow tasks without cases</span>
                    <span className="block text-gray-600 mt-1">
                      Import tasks even if the case they reference doesn't exist yet.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={runPreview}
                disabled={isLoading || Object.values(columnMapping).filter(v => v).length === 0}
                className={btnPrimary}
              >
                {isLoading ? 'Processing...' : 'Preview Import →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Preview Results */}
      {currentStep === 3 && previewResults && (
        <div className="mt-12 space-y-6">
          <div className={`${sectionCard} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Preview Results</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Review the validation results before committing. Fix any errors and re-upload if needed.
                </p>
              </div>
              <button onClick={() => setCurrentStep(2)} className={btnSecondary}>
                ← Back to Mapping
              </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Processed</p>
                <p className="text-2xl font-bold text-gray-900">{previewResults.processed || 0}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-xs uppercase tracking-wide text-green-700 mb-1">Will Create</p>
                <p className="text-2xl font-bold text-green-700">{previewResults.created || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-xs uppercase tracking-wide text-blue-700 mb-1">Will Update</p>
                <p className="text-2xl font-bold text-blue-700">{previewResults.updated || 0}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-xs uppercase tracking-wide text-red-700 mb-1">Errors</p>
                <p className="text-2xl font-bold text-red-700">{previewResults.errors?.length || 0}</p>
              </div>
            </div>

            {/* Missing Dependencies Warning */}
            {missingDependencies && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="text-sm font-semibold text-yellow-900 mb-2">⚠️ Missing Dependencies Detected</h4>
                <p className="text-sm text-yellow-800 mb-3">
                  Some records reference entities that don't exist yet. You can:
                </p>
                <ul className="list-disc pl-5 text-sm text-yellow-800 space-y-1">
                  <li>Download the list of missing items and import them first</li>
                  <li>Enable auto-creation options (if available for this entity type)</li>
                  <li>Continue anyway and link them manually later</li>
                </ul>
                <button className={`${btnSecondary} mt-3`} onClick={downloadMissingDependenciesCsv}>
                  Download Missing Items CSV
                </button>
              </div>
            )}

            {/* Row-level Results */}
            {previewResults.row_results && previewResults.row_results.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">Row-by-Row Results</h4>
                  <button onClick={downloadErrorCsv} className={btnSecondary}>
                    Download Error Rows
                  </button>
                </div>
                <div className="max-h-96 overflow-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewResults.row_results.slice(0, 200).map((row, idx) => (
                        <tr key={idx} className={
                          row.status === 'created' ? 'bg-green-50' :
                          row.status === 'updated' ? 'bg-blue-50' :
                          row.status === 'error' ? 'bg-red-50' :
                          row.status === 'skipped' ? 'bg-yellow-50' : ''
                        }>
                          <td className="px-4 py-2 text-sm text-gray-900">{row.row}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              row.status === 'created' ? 'bg-green-100 text-green-800' :
                              row.status === 'updated' ? 'bg-blue-100 text-blue-800' :
                              row.status === 'error' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">{row.id || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{row.message || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewResults.row_results.length > 200 && (
                    <div className="text-xs text-gray-600 px-4 py-2 bg-gray-50 border-t">
                      Showing first 200 of {previewResults.row_results.length} rows
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors List */}
            {previewResults.errors && previewResults.errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-sm font-semibold text-red-900 mb-2">Errors Encountered</h4>
                <ul className="list-disc pl-5 text-sm text-red-800 space-y-1 max-h-60 overflow-auto">
                  {previewResults.errors.slice(0, 50).map((err, idx) => (
                    <li key={idx}>{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                  ))}
                  {previewResults.errors.length > 50 && (
                    <li className="text-red-600">...and {previewResults.errors.length - 50} more errors</li>
                  )}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t">
              <button onClick={resetWizard} className={btnSecondary}>
                Start Over
              </button>
              <div className="flex gap-3">
                <button onClick={runPreview} disabled={isLoading} className={btnSecondary}>
                  Re-validate
                </button>
                <button
                  onClick={runCommit}
                  disabled={isLoading || (previewResults.errors && previewResults.errors.length > 0)}
                  className={btnSuccess}
                >
                  {isLoading ? 'Importing...' : '✓ Commit Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {currentStep === 4 && previewResults && (
        <div className="mt-12 space-y-6">
          <div className={`${sectionCard} p-6`}>
            <div className="text-center py-8">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4">
                <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Import Complete!</h3>
              <p className="text-gray-600 mb-6">
                Successfully imported <strong>{previewResults.created || 0}</strong> new {entity} and updated <strong>{previewResults.updated || 0}</strong> existing records.
              </p>

              <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase">Processed</p>
                  <p className="text-2xl font-bold text-gray-900">{previewResults.processed || 0}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs text-green-700 uppercase">Created</p>
                  <p className="text-2xl font-bold text-green-700">{previewResults.created || 0}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-700 uppercase">Updated</p>
                  <p className="text-2xl font-bold text-blue-700">{previewResults.updated || 0}</p>
                </div>
              </div>

              <button onClick={resetWizard} className={btnPrimary}>
                Import More Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress indicator */}
      {isLoading && progress > 0 && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80">
          <p className="text-sm font-medium text-gray-900 mb-2">Processing...</p>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">{progress}% complete</p>
        </div>
      )}
    </div>
  );
}
